import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Link } from 'react-router-dom';
import { Box, Card, CardHeader, Chip, IconButton } from '@mui/material';
import { Add, DirectionsBoat, Edit } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { CompanyModel, CompanyType } from 'models/CompanyModel';
import { VesselModel } from 'models/VesselModel';
import useCompanyModals from 'modules/companies/hooks/useCompanyModals';
import useVesselModals from 'modules/vessels/hooks/useVesselModals';
import companyList from 'recoil/atoms/companyList';
import auth from 'recoil/atoms/auth';


const VesselOwnersInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const companies = useRecoilValue(companyList(CompanyType.VesselOwner));
    const {openAddCompanyModal, openEditCompanyModal, openVesselSelectModal} = useCompanyModals(CompanyType.VesselOwner);
    const { openEditVesselModal } = useVesselModals();
    const addButton = my.permissions.has('companies create') ? (
        <IconButton
            aria-label="add transport agent"
            onClick={openAddCompanyModal}
        >
            <Add/>
        </IconButton>
    ) : null;

    const columns: GridColDef<CompanyModel>[] = [
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'vessels',
            headerName: t('vessels'),
            flex: 1,
            minWidth: 200,
            renderCell: params => (
                <>
                    {params.value?.map((vessel: VesselModel) => <Chip
                        key={vessel.id}
                        sx={{cursor: 'pointer'}}
                        label={vessel.name}
                        size="small"
                        onClick={() => openEditVesselModal(vessel)}
                    />)}
                </>
            ),
        },
        {
            field: 'usersCount',
            headerName: t('users'),
            width: 100,
            renderCell: params => (
                <Chip
                    sx={{cursor: 'pointer'}}
                    label={params.value}
                    color={params.value > 1 ? 'info' : undefined}
                    variant={params.value > 1 ? 'outlined' : undefined}
                    size="small"
                    component={Link}
                    to={`/users?company=${params.row.id}`}
                />
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => [
                <GridActionsCellItem
                    icon={<Edit/>}
                    label={t('edit')}
                    onClick={() => openEditCompanyModal(params.row)}
                />,
                <GridActionsCellItem
                    icon={<DirectionsBoat/>}
                    label={t('vessels')}
                    onClick={() => openVesselSelectModal(params.row)}
                />,
            ],
        },
    ];

    return (
        <Card>
            <CardHeader
                title={t('vessel_owners')}
                action={addButton}
            />
            <Box sx={{height: 'calc(100vh - 196px)', padding: '0 1rem'}}>
                <DataGrid
                    rows={companies}
                    columns={columns}
                />
            </Box>
        </Card>
    );
};

const VesselOwners = () => {
    return (
        <Suspense fallback={null}>
            <VesselOwnersInner/>
        </Suspense>
    )
}

export default VesselOwners;
